import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useSelector} from 'react-redux'

import {isAdmin} from 'common/HelperFunctions'

import {CURRICULUM_DATA_SOURCE, setCachedStatus} from '../utils/access'
import {isProcessing} from '../utils/bookStatus'
import {curriculumApi, fetchCover, fetchPageImage, type CurriculumStatus, type ExtractedPage} from './curriculumApi'

/** Poll interval while the server works on a book (handoff §5.3). */
const POLL_MS = 5000

/**
 * Every query key carries the signed-in session, so another user signing in on the same tab never
 * sees the previous user's books or permissions from the cache.
 */
const useSession = (): string =>
  useSelector((state: {userReducer: {accessToken: string | null}}) => state.userReducer.accessToken ?? '').slice(-16)

const OFF: CurriculumStatus = {enabled: false, can_manage: false, can_ask: false}

/**
 * The feature check (`GET /status`). Off when the API says so or the call fails (a 404 means the
 * learning assistant is not switched on for this school). Demo mode skips the call.
 */
export const useCurriculumStatus = (enabled = true) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'status'],
    queryFn: async () => {
      const status =
        CURRICULUM_DATA_SOURCE === 'demo'
          ? {enabled: true, can_ask: true, can_manage: isAdmin()}
          : await curriculumApi.status().catch(() => OFF)
      setCachedStatus(status)
      return status
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  })
}

/** Books: every book for managers, ready books only for learners. Polled while one is processing. */
export const useBooks = (enabled = true) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'books'],
    queryFn: curriculumApi.books,
    enabled: enabled && CURRICULUM_DATA_SOURCE === 'api',
    refetchInterval: (query) => (query.state.data?.some(isProcessing) ? POLL_MS : false),
  })
}

/** One book with its progress; polled while the server works on it. */
export const useBook = (id: number | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'book', id],
    queryFn: () => curriculumApi.book(id as number),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data && isProcessing(query.state.data) ? POLL_MS : false),
  })
}

export const useStorage = (enabled = true) => {
  const session = useSession()
  return useQuery({queryKey: ['curriculum', session, 'storage'], queryFn: curriculumApi.storage, enabled})
}

export const useStructure = (id: number | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'structure', id],
    queryFn: () => curriculumApi.structure(id as number),
    enabled: id !== null,
  })
}

/** Review list; polled while the background quality check is still adding flags. */
export const usePages = (id: number | null, poll = false) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'pages', id],
    queryFn: () => curriculumApi.pages(id as number),
    enabled: id !== null,
    refetchInterval: poll ? POLL_MS : false,
  })
}

export const usePage = (id: number | null, pdfPage: number | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'page', id, pdfPage],
    queryFn: () => curriculumApi.page(id as number, pdfPage as number),
    enabled: id !== null && pdfPage !== null,
    // The signed image URL lasts ~15 min; refetch well before it expires.
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  })
}

/** Mutations of the Book Library; each refreshes what it changed. */
export const useBookActions = () => {
  const client = useQueryClient()
  const session = useSession()
  const refresh = (id: number) => {
    for (const part of ['books', 'book', 'pages', 'structure', 'page']) {
      client.invalidateQueries({queryKey: part === 'books' ? ['curriculum', session, part] : ['curriculum', session, part, id]})
    }
  }

  return {
    resume: useMutation({mutationFn: curriculumApi.resume, onSuccess: (_book, id) => refresh(id)}),
    remove: useMutation({mutationFn: curriculumApi.deleteBook, onSuccess: (_result, id) => refresh(id)}),
    publish: useMutation({mutationFn: curriculumApi.publish, onSuccess: (_book, id) => refresh(id)}),
    confirmStructure: useMutation({
      mutationFn: (args: {id: number; body: Parameters<typeof curriculumApi.confirmStructure>[1]}) =>
        curriculumApi.confirmStructure(args.id, args.body),
      onSuccess: (_book, args) => refresh(args.id),
    }),
    approve: useMutation({
      mutationFn: (args: {id: number; pages: number[] | 'all'}) => curriculumApi.approve(args.id, args.pages),
      onSuccess: (_result, args) => refresh(args.id),
    }),
    savePage: useMutation({
      mutationFn: (args: {id: number; pdfPage: number; extracted: ExtractedPage}) =>
        curriculumApi.savePage(args.id, args.pdfPage, args.extracted),
      onSuccess: (_result, args) => refresh(args.id),
    }),
    /** Re-reads the book list (after an upload registered a new book). */
    refreshBooks: () => client.invalidateQueries({queryKey: ['curriculum', session, 'books']}),
  }
}

/**
 * A printed page's image from S3 (`GET /books/:id/pages/:printed/image`, which redirects to a
 * signed URL and needs the auth headers, so it is fetched as a blob). Cached per page.
 */
export const usePageImage = (bookId: number | null, printedPage: number | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'page-image', bookId, printedPage],
    queryFn: () => fetchPageImage(bookId as number, printedPage as number),
    enabled: bookId !== null && printedPage !== null && printedPage > 0,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  })
}

/** Units, covers and starter questions of a ready book (`GET /books/:id/units`, everyone). */
export const useBookUnits = (bookId: number | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'units', bookId],
    queryFn: () => curriculumApi.units(bookId as number),
    enabled: bookId !== null && CURRICULUM_DATA_SOURCE === 'api',
    // Signed cover URLs last ~15 min; refetch before they expire.
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  })
}

/** A fresh cover through `cover_path`, used only when the signed URL failed to load. */
export const useCoverFallback = (coverPath: string | null) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'cover', coverPath],
    queryFn: () => fetchCover(coverPath as string),
    enabled: coverPath !== null,
    staleTime: Infinity,
    retry: false,
  })
}

/** Usage of a book over the last `days` days (`GET /books/:id/insights`, school admins). */
export const useBookInsights = (bookId: number | null, days: number, enabled = true) => {
  const session = useSession()
  return useQuery({
    queryKey: ['curriculum', session, 'insights', bookId, days],
    queryFn: () => curriculumApi.insights(bookId as number, days),
    enabled: enabled && bookId !== null,
  })
}
